import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { isArray } from "lodash";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Menu, MenuItem, TextField, InputAdornment, CircularProgress } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ImageIcon from "@material-ui/icons/Image";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import VisibilityIcon from "@material-ui/icons/Visibility";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import MovieIcon from "@material-ui/icons/Movie";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import "./quickRepliesLayout.css";

// Mantém os mesmos eventos, paginação e operações CRUD já existentes.
const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const items = action.payload;
    const next = [...state];
    if (isArray(items)) {
      items.forEach(item => {
        const index = next.findIndex(previous => previous.id === item.id);
        if (index === -1) next.push(item);
        else next[index] = item;
      });
    }
    return next;
  }
  if (action.type === "UPDATE_QUICKMESSAGES") {
    const next = [...state];
    const index = next.findIndex(item => item.id === action.payload.id);
    if (index !== -1) next[index] = action.payload;
    else next.unshift(action.payload);
    return next;
  }
  if (action.type === "DELETE_QUICKMESSAGE") {
    return state.filter(item => item.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const extensionOf = name => {
  const cleanName = String(name || "").split("?")[0].split("#")[0];
  const dot = cleanName.lastIndexOf(".");
  return dot === -1 ? "" : cleanName.slice(dot + 1).toLowerCase();
};

const mediaKind = item => {
  const ext = extensionOf(item.mediaName || item.mediaPath);
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "avif"].includes(ext)) return "image";
  if (["mp3", "ogg", "oga", "wav", "m4a", "aac", "opus", "flac"].includes(ext)) return "audio";
  if (ext === "pdf") return "pdf";
  if (["mp4", "webm", "mov", "m4v"].includes(ext)) return "video";
  return "document";
};

const timeLabel = seconds => {
  const value = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, "0")}`;
};

const QuickReplyAudio = ({ src, name }) => {
  const player = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  useEffect(() => {
    setPlaying(false);
    setPosition(0);
    setDuration(0);
    setSpeed(1);
  }, [src]);

  const toggle = () => {
    if (!player.current) return;
    if (player.current.paused) {
      const result = player.current.play();
      if (result && typeof result.catch === "function") result.catch(() => setPlaying(false));
    } else {
      player.current.pause();
    }
  };
  const changeSpeed = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(next);
    if (player.current) player.current.playbackRate = next;
  };
  return (
    <div className="aqr-audio" onClick={event => event.stopPropagation()}>
      <audio
        ref={player}
        src={src}
        preload="metadata"
        onLoadedMetadata={event => setDuration(event.currentTarget.duration || 0)}
        onTimeUpdate={event => setPosition(event.currentTarget.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPosition(0); }}
      />
      <div className="aqr-audio-controls">
        <button type="button" className="aqr-play" onClick={toggle} title={playing ? "Pausar áudio" : "Reproduzir áudio"} aria-label={playing ? "Pausar áudio" : "Reproduzir áudio"}>
          {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </button>
        <div className="aqr-audio-progress">
          <input type="range" min="0" max={duration || 1} step="0.1" value={Math.min(position, duration || 1)}
            style={{ backgroundSize: `${duration ? (position / duration) * 100 : 0}% 100%` }}
            onChange={event => { if (player.current) player.current.currentTime = Number(event.target.value); setPosition(Number(event.target.value)); }}
            aria-label="Avançar ou voltar no áudio" />
          <div className="aqr-audio-clock">{timeLabel(position)} / {timeLabel(duration)}</div>
        </div>
        <button type="button" className="aqr-speed" onClick={changeSpeed} title="Alterar velocidade do áudio">{speed}×</button>
      </div>
      <div className="aqr-file-name" title={name}>{name}</div>
    </div>
  );
};

const AttachmentPreview = ({ message, onPreview }) => {
  const url = message.mediaPath;
  const name = message.mediaName || "Arquivo anexado";
  if (!url) {
    return (
      <div className="aqr-empty-file">
        <AttachFileIcon fontSize="small" />
        <div><strong>Sem anexo</strong><span>Esta resposta não possui arquivos anexados.</span></div>
      </div>
    );
  }
  const type = mediaKind(message);
  if (type === "image") return (
    <div className="aqr-attachment">
      <div className="aqr-media-label"><ImageIcon fontSize="small" /> Imagem anexada</div>
      <button className="aqr-image-button" type="button" onClick={() => onPreview({ type, url, name })} title="Visualizar imagem">
        <img src={url} alt={name} loading="lazy" />
      </button>
      <span className="aqr-file-name" title={name}>{name}</span>
    </div>
  );
  if (type === "audio") return (
    <div className="aqr-attachment">
      <div className="aqr-media-label"><AudiotrackIcon fontSize="small" /> Áudio anexado</div>
      <QuickReplyAudio src={url} name={name} />
    </div>
  );
  if (type === "pdf") return (
    <div className="aqr-attachment">
      <div className="aqr-media-label aqr-pdf-label"><PictureAsPdfIcon fontSize="small" /> PDF anexado</div>
      <button type="button" className="aqr-pdf-preview" onClick={() => onPreview({ type, url, name })} title="Visualizar PDF">
        <iframe title={`Prévia de ${name}`} src={`${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`} tabIndex="-1" />
        <span className="aqr-pdf-overlay"><VisibilityIcon fontSize="small" /> Visualizar PDF</span>
      </button>
      <div className="aqr-file-row"><span className="aqr-file-name" title={name}>{name}</span><button type="button" onClick={() => onPreview({ type, url, name })}>Visualizar</button></div>
    </div>
  );
  if (type === "video") return (
    <div className="aqr-attachment">
      <div className="aqr-media-label"><MovieIcon fontSize="small" /> Vídeo anexado</div>
      <video controls preload="metadata" src={url} className="aqr-video" />
      <span className="aqr-file-name" title={name}>{name}</span>
    </div>
  );
  return (
    <div className="aqr-attachment">
      <div className="aqr-media-label"><InsertDriveFileIcon fontSize="small" /> Documento anexado</div>
      <a className="aqr-document" href={url} target="_blank" rel="noopener noreferrer" title={`Abrir ${name}`}>
        <div className="aqr-doc-icon"><InsertDriveFileIcon /></div>
        <div className="aqr-doc-meta"><strong title={name}>{name}</strong><span>{extensionOf(name).toUpperCase() || "ARQUIVO"} · Abrir ou baixar</span></div>
        <GetAppIcon fontSize="small" />
      </a>
    </div>
  );
};

const Quickemessages = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const [preview, setPreview] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuMessage, setMenuMessage] = useState(null);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => { fetchQuickemessages(); }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onQuickMessageEvent = data => {
      if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      if (data.action === "delete") dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
    };
    socket.on(`company-${companyId}-quickmessage`, onQuickMessageEvent);
    return () => socket.off(`company-${companyId}-quickmessage`, onQuickMessageEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };
  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };
  const handleEditQuickemessage = quickemessage => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };
  const handleDeleteQuickemessage = async id => {
    try {
      await api.delete(`/quick-messages/${id}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };
  const handleScroll = event => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(prev => prev + 1);
  };
  const openDelete = message => {
    setDeletingQuickemessage(message);
    setConfirmModalOpen(true);
  };
  const openMenu = (event, message) => {
    setMenuAnchor(event.currentTarget);
    setMenuMessage(message);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuMessage(null); };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingQuickemessage && `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >{i18n.t("quickMessages.confirmationModal.deleteMessage")}</ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => { setPageNumber(1); fetchQuickemessages(); }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="md" aria-labelledby="aqr-preview-title">
        <DialogTitle id="aqr-preview-title" disableTypography>
          <div className="aqr-preview-title"><strong>{preview && preview.name}</strong><IconButton onClick={() => setPreview(null)} aria-label="Fechar prévia"><CloseIcon /></IconButton></div>
        </DialogTitle>
        <DialogContent dividers className="aqr-preview-dialog">
          {preview && preview.type === "image" && <img src={preview.url} alt={preview.name} />}
          {preview && preview.type === "pdf" && <iframe title={preview.name} src={preview.url} />}
        </DialogContent>
        <DialogActions><Button href={preview ? preview.url : "#"} target="_blank" rel="noopener noreferrer" color="primary" startIcon={<GetAppIcon />}>Abrir / baixar arquivo</Button><Button onClick={() => setPreview(null)}>Fechar</Button></DialogActions>
      </Dialog>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} keepMounted>
        <MenuItem onClick={() => { const item = menuMessage; closeMenu(); if (item) handleEditQuickemessage(item); }}><EditIcon fontSize="small" className="aqr-menu-icon" /> Editar</MenuItem>
        <MenuItem onClick={() => { const item = menuMessage; closeMenu(); if (item) openDelete(item); }}><DeleteOutlineIcon fontSize="small" className="aqr-menu-icon" /> Excluir</MenuItem>
      </Menu>
      <div className="aqr-page-header">
        <div className="aqr-heading">
          <span className="aqr-heading-icon"><FlashOnIcon /></span>
          <div><h1>Respostas Rápidas</h1><p>Gerencie mensagens prontas e visualize anexos rapidamente.</p></div>
        </div>
        <div className="aqr-toolbar">
          <TextField className="aqr-search" variant="outlined" size="small" placeholder="Pesquisar por atalho..."
            type="search" value={searchParam} onChange={event => setSearchParam(event.target.value.toLowerCase())}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Button className="aqr-add" variant="contained" color="primary" onClick={handleOpenQuickMessageDialog} startIcon={<AddIcon />}>
            {i18n.t("quickMessages.buttons.add")}
          </Button>
        </div>
      </div>
      <div className="aqr-scroll" onScroll={handleScroll}>
        <div className="aqr-grid">
          {quickemessages.map(message => (
            <article className="aqr-card" key={message.id}>
              <div className="aqr-card-top">
                <span className="aqr-code" title={message.shortcode}><FlashOnIcon fontSize="small" /> <span>Atalho: /{message.shortcode}</span></span>
                <IconButton className="aqr-card-more" size="small" aria-label={`Opções da resposta ${message.shortcode}`} onClick={event => openMenu(event, message)}><MoreVertIcon fontSize="small" /></IconButton>
              </div>
              <div className="aqr-message" title={message.message || ""}>{message.message || "Sem texto na resposta."}</div>
              <div className="aqr-card-media"><AttachmentPreview message={message} onPreview={setPreview} /></div>
              <div className="aqr-card-bottom">
                <span className={message.geral === true ? "aqr-permission aqr-allow" : "aqr-permission aqr-restrict"}>
                  {message.geral === true ? "Pode editar" : "Edição restrita"}
                </span>
                <div className="aqr-card-actions">
                  <button type="button" onClick={() => handleEditQuickemessage(message)} className="aqr-edit"><EditIcon fontSize="small" /> Editar</button>
                  <button type="button" onClick={() => openDelete(message)} className="aqr-delete"><DeleteOutlineIcon fontSize="small" /> Excluir</button>
                </div>
              </div>
            </article>
          ))}
        </div>
        {loading && <div className="aqr-loading"><CircularProgress size={22} /> Carregando respostas...</div>}
        {!loading && quickemessages.length === 0 && <div className="aqr-empty">Nenhuma resposta rápida encontrada.</div>}
      </div>
    </MainContainer>
  );
};

export default Quickemessages;
